import { useState, useEffect } from 'react';
import { Button } from './Button';
import { useLanguage } from '../hooks/useLanguage';
import { useToast } from '../hooks/useToast';

export const ActivityScheduleSettings = () => {
  const { t } = useLanguage();
  const toast = useToast();

  const [settings, setSettings] = useState({
    workingDays: {
      sunday: { enabled: true, startTime: '09:00', endTime: '18:00' },
      monday: { enabled: true, startTime: '09:00', endTime: '18:00' },
      tuesday: { enabled: true, startTime: '09:00', endTime: '18:00' },
      wednesday: { enabled: true, startTime: '09:00', endTime: '18:00' },
      thursday: { enabled: true, startTime: '09:00', endTime: '18:00' },
      friday: { enabled: true, startTime: '09:00', endTime: '18:00' },
      saturday: { enabled: false, startTime: '09:00', endTime: '18:00' },
    },
    defaultCalendarView: 'dayGridMonth', // dayGridMonth, timeGridWeek, timeGridDay
  });

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('activity_schedule_settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
      } catch (error) {
        console.error('Failed to parse activity schedule settings:', error);
      }
    }
  }, []);

  const handleDayToggle = (day) => {
    setSettings((prev) => ({
      ...prev,
      workingDays: {
        ...prev.workingDays,
        [day]: {
          ...prev.workingDays[day],
          enabled: !prev.workingDays[day].enabled,
        },
      },
    }));
  };

  const handleTimeChange = (day, field, value) => {
    setSettings((prev) => ({
      ...prev,
      workingDays: {
        ...prev.workingDays,
        [day]: {
          ...prev.workingDays[day],
          [field]: value,
        },
      },
    }));
  };

  const handleDefaultViewChange = (view) => {
    setSettings((prev) => ({
      ...prev,
      defaultCalendarView: view,
    }));
  };

  const handleSave = () => {
    try {
      localStorage.setItem('activity_schedule_settings', JSON.stringify(settings));

      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('activityScheduleSettingsUpdated', {
        detail: settings
      }));

      toast.success(t('settingsSaved') || 'Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(t('failedToSaveSettings') || 'Failed to save settings');
    }
  };

  const handleReset = () => {
    const defaultSettings = {
      workingDays: {
        sunday: { enabled: true, startTime: '09:00', endTime: '18:00' },
        monday: { enabled: true, startTime: '09:00', endTime: '18:00' },
        tuesday: { enabled: true, startTime: '09:00', endTime: '18:00' },
        wednesday: { enabled: true, startTime: '09:00', endTime: '18:00' },
        thursday: { enabled: true, startTime: '09:00', endTime: '18:00' },
        friday: { enabled: true, startTime: '09:00', endTime: '18:00' },
        saturday: { enabled: false, startTime: '09:00', endTime: '18:00' },
      },
      defaultCalendarView: 'dayGridMonth',
    };
    setSettings(defaultSettings);
    localStorage.setItem('activity_schedule_settings', JSON.stringify(defaultSettings));
    window.dispatchEvent(new CustomEvent('activityScheduleSettingsUpdated', {
      detail: defaultSettings
    }));
    toast.success(t('settingsReset') || 'Settings reset to defaults');
  };

  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              {t('activityScheduleSettingsInfo') || 'Configure working days, hours for activities, and default calendar view.'}
            </p>
          </div>
        </div>
      </div>

      {/* Default Calendar View */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('defaultCalendarView') || 'Default Calendar View'}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('defaultCalendarViewHelp') || 'Choose the default view when opening the calendar.'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            onClick={() => handleDefaultViewChange('dayGridMonth')}
            className={`p-4 border-2 rounded-lg text-center transition-all ${
              settings.defaultCalendarView === 'dayGridMonth'
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="text-2xl mb-2">📅</div>
            <div className="font-medium">{t('monthView') || 'Month View'}</div>
            <div className="text-xs text-gray-500 mt-1">{t('monthViewDesc') || 'See all events in a month'}</div>
          </button>
          <button
            onClick={() => handleDefaultViewChange('timeGridWeek')}
            className={`p-4 border-2 rounded-lg text-center transition-all ${
              settings.defaultCalendarView === 'timeGridWeek'
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="text-2xl mb-2">📆</div>
            <div className="font-medium">{t('weekView') || 'Week View'}</div>
            <div className="text-xs text-gray-500 mt-1">{t('weekViewDesc') || 'See events by week'}</div>
          </button>
          <button
            onClick={() => handleDefaultViewChange('timeGridDay')}
            className={`p-4 border-2 rounded-lg text-center transition-all ${
              settings.defaultCalendarView === 'timeGridDay'
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="text-2xl mb-2">📋</div>
            <div className="font-medium">{t('dayView') || 'Day View'}</div>
            <div className="text-xs text-gray-500 mt-1">{t('dayViewDesc') || 'See events by day'}</div>
          </button>
        </div>
      </div>

      {/* Working Days and Hours */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('workingDaysAndHours') || 'Working Days and Hours'}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('workingDaysAndHoursHelp') || 'Set which days and hours activities can be scheduled. Disabled days will be highlighted in the calendar.'}
        </p>
        <div className="space-y-3">
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className={`border rounded-lg p-4 transition-all ${
                settings.workingDays[day].enabled
                  ? 'border-gray-300 bg-white'
                  : 'border-gray-200 bg-gray-50 opacity-60'
              }`}
            >
              <div className="flex flex-col gap-3">
                {/* Day Toggle */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.workingDays[day].enabled}
                    onChange={() => handleDayToggle(day)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                  />
                  <span className="font-medium text-gray-900 capitalize">
                    {t(day) || day}
                  </span>
                </label>

                {/* Time Range */}
                {settings.workingDays[day].enabled && (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 flex-1 w-full sm:w-auto">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <label className="text-sm text-gray-600 whitespace-nowrap min-w-[40px]">
                        {t('from') || 'From'}:
                      </label>
                      <input
                        type="time"
                        value={settings.workingDays[day].startTime}
                        onChange={(e) => handleTimeChange(day, 'startTime', e.target.value)}
                        step="1800"
                        className="flex-1 sm:flex-initial px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <label className="text-sm text-gray-600 whitespace-nowrap min-w-[40px]">
                        {t('to') || 'To'}:
                      </label>
                      <input
                        type="time"
                        value={settings.workingDays[day].endTime}
                        onChange={(e) => handleTimeChange(day, 'endTime', e.target.value)}
                        step="1800"
                        className="flex-1 sm:flex-initial px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <Button variant="secondary" onClick={handleReset} className="w-full sm:w-auto">
          {t('resetToDefaults') || 'Reset to Defaults'}
        </Button>
        <Button onClick={handleSave} className="w-full sm:w-auto">
          {t('saveSettings') || 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};
